/**
 * PaymentPageV2 - Main entry point for the new payment page
 *
 * Features:
 * - Wraps with Stripe Elements provider
 * - Two-column responsive layout (desktop)
 * - Single column with correct ordering (mobile)
 * - Sticky CTA button on mobile only
 */

import { Elements } from '@stripe/react-stripe-js';
import { useSearchParams } from 'react-router-dom';
import usePaymentController, { useStripeLoadStatus } from '@/app/components/payment/payment.controller';
import { StripeLoadError } from '@/app/components/payment/StripeLoadError';
import useCollectPaymentMethod from '@/app/components/payment/components/method/payment-method-v2.controller';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import {
  StripeAmountProvider,
  useStripeAmount,
} from '@/app/components/ppra/components/ppra-payment-page/contexts/StripeAmountContext';
import { useStripeAmountUpdater } from '@/app/components/ppra/components/ppra-payment-page/hooks/useStripeAmountUpdater';

import { PaymentPageLayout } from './PaymentPageLayout';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FeaturesList } from './components/left-column/FeaturesList';
import { TestimonialsSection } from './components/left-column/TestimonialsSection';
import { PrivacySection } from './components/left-column/PrivacySection';
import { FAQSection } from './components/FAQSection';
import { PaymentCard } from './components/right-column/PaymentCard';
import { FooterLinks } from './components/FooterLinks';
import { FooterDisclaimer } from './components/FooterDisclaimer';
import { ContactButton } from './components/ContactButton';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { FEATURE_GROUPS, MULTI_DISEASE_FEATURE_GROUPS } from './constants';

import {
  PricingHeader,
  EmailField,
  NameField,
  CardSection,
  ExpressCheckoutSection,
  ZipCodeField,
  CouponInput,
  SubmitButton,
  TrialChargeNotice,
  PaymentTrustBadges,
  LegalDisclaimer,
  HsaFsaDisclaimer,
} from '@/components/payment-form';
import { useEnsureFunnelSession, useFunnelLead } from '@/hooks/useFunnelLead';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { usePurchaseModel } from '@/hooks/use-purchase-model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COHORT_STORAGE_KEY, POSITIONING_VARIANT_STORAGE_KEY, POSITIONING_VARIANTS, type PositioningVariant } from '@/constants/payment';

/**
 * Get cohort from URL param or sessionStorage (for redirect preservation).
 * URL param takes precedence; if found, it's stored for later retrieval.
 * Defaults to 'cancer' if no cohort is specified.
 */
function getCohort(searchParams: URLSearchParams): string {
  const urlCohort = searchParams.get('cohort');

  if (urlCohort) {
    // Store in sessionStorage for redirect preservation
    sessionStorage.setItem(COHORT_STORAGE_KEY, urlCohort);
    return urlCohort;
  }

  // Check sessionStorage (after 3DS redirect)
  const storedCohort = sessionStorage.getItem(COHORT_STORAGE_KEY);
  if (storedCohort) {
    return storedCohort;
  }

  // Default to cancer cohort and persist for redirect preservation
  sessionStorage.setItem(COHORT_STORAGE_KEY, 'cancer');
  return 'cancer';
}

/**
 * Resolve positioning variant from `?positioning=` URL param, falling back to
 * sessionStorage (for 3DS/Stripe redirect preservation), then 'default'.
 * Mirrors the pattern used for cohort.
 */
function getPositioningVariant(searchParams: URLSearchParams): PositioningVariant {
  const param = searchParams.get('positioning');

  const isValid = (v: string | null): v is PositioningVariant =>
    v !== null && (POSITIONING_VARIANTS as readonly string[]).includes(v);

  if (isValid(param)) {
    sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, param);
    return param;
  }

  const stored = sessionStorage.getItem(POSITIONING_VARIANT_STORAGE_KEY);
  if (isValid(stored)) {
    return stored;
  }

  sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, 'default');
  return 'default';
}

/**
 * PaymentForm - The actual payment form with all fields
 */
function PaymentForm() {
  useStripeAmountUpdater();
  const [searchParams] = useSearchParams();

  // Detect multi-disease cohort from URL params or sessionStorage
  const cohort = getCohort(searchParams);
  const isMultiDiseaseCohort = cohort === 'multi-disease';
  const featureGroups = isMultiDiseaseCohort ? MULTI_DISEASE_FEATURE_GROUPS : FEATURE_GROUPS;

  // Resolve positioning variant from URL param / sessionStorage / default
  const positioningVariant = getPositioningVariant(searchParams);

  // Set Amplitude user properties for cohort + positioning variant
  const { setMultiVerticalExperimentProperty, setPositioningVariantProperty } = useAnalyticsCalls();
  const hasSetCohortRef = useRef(false);
  const hasSetPositioningRef = useRef(false);
  useEffect(() => {
    if (isMultiDiseaseCohort && !hasSetCohortRef.current) {
      hasSetCohortRef.current = true;
      setMultiVerticalExperimentProperty();
    }
  }, [isMultiDiseaseCohort, setMultiVerticalExperimentProperty]);
  useEffect(() => {
    if (!hasSetPositioningRef.current) {
      hasSetPositioningRef.current = true;
      setPositioningVariantProperty(positioningVariant);
    }
  }, [positioningVariant, setPositioningVariantProperty]);

  const {
    coupon,
    setCoupon,
    couponValidationError,
    isSubmitting,
    getPrice,
    handleSubmit,
    handleExpressCheckout,
    isTrial,
    isPriceLoading,
    priceRecurring,
    priceSubtotal,
    email: sessionEmail,
    form,
  } = useCollectPaymentMethod();

  // Ensure a funnel session exists before either prefill or blur capture
  // can run. Users entering at /payment directly (no /join upstream) get a
  // session minted here; users who came through /join already have one and
  // we reuse it.
  useEnsureFunnelSession();

  // Funnel lead is the single source of truth for email + name. Hydrates
  // from GET /v1/funnel-sessions/me/lead once the session exists.
  const { lead: funnelLead, updateLead } = useFunnelLead();
  useEffect(() => {
    if (!funnelLead) return;
    const composedName = [funnelLead.firstName, funnelLead.lastName].filter(Boolean).join(' ').trim();
    if (composedName && !form.getValues('name')) {
      form.setValue('name', composedName);
    }
    if (funnelLead.email && !form.getValues('email')) {
      form.setValue('email', funnelLead.email);
    }
  }, [funnelLead, form]);

  // Capture email blur into the funnel lead. Same code path as JoinStep1's
  // email field — direct-to-/payment users land in `funnel_leads` just like
  // /join users do.
  const handleLeadCapture = useCallback(
    async (email: string) => {
      try {
        await updateLead({ email });
      } catch (error) {
        console.error('Failed to capture funnel email:', error);
      }
    },
    [updateLead]
  );

  // Capture name blur into the funnel lead. The payment form has a single
  // `name` field; we split on the last whitespace into first/last so the
  // funnel_leads row matches the shape collected on /join.
  const handleNameCapture = useCallback(
    async (fullName: string) => {
      const trimmed = fullName.trim();
      if (!trimmed) return;
      const lastSpace = trimmed.lastIndexOf(' ');
      const firstName = lastSpace === -1 ? trimmed : trimmed.slice(0, lastSpace).trim();
      const lastName = lastSpace === -1 ? undefined : trimmed.slice(lastSpace + 1).trim() || undefined;
      try {
        await updateLead({ firstName, lastName });
      } catch (error) {
        console.error('Failed to capture funnel name:', error);
      }
    },
    [updateLead]
  );

  // Regular annual price (without discount) - derived from API subtotal
  const regularAnnualPrice =
    typeof priceSubtotal === 'number' ? Math.round(priceSubtotal / 100) : undefined;

  // Discounted first charge (when coupon applied) - from recurring.amount
  const discountedFirstCharge =
    typeof priceRecurring?.amount === 'number'
      ? Math.round(priceRecurring.amount / 100)
      : undefined;

  // Determine if a coupon discount is active
  const isCouponApplied =
    discountedFirstCharge !== undefined &&
    regularAnnualPrice !== undefined &&
    discountedFirstCharge < regularAnnualPrice;

  const discountPercent =
    isCouponApplied && regularAnnualPrice
      ? Math.round(((regularAnnualPrice - discountedFirstCharge) / regularAnnualPrice) * 100)
      : 0;

  // Coupon input expand/collapse state
  const [isCouponExpanded, setIsCouponExpanded] = useState(false);

  // Handle removing an applied coupon
  const handleRemoveCoupon = useCallback(() => {
    setCoupon('');
    setIsCouponExpanded(false);
    // Pass empty coupon explicitly to avoid stale closure reading old tempCoupon
    getPrice('');
  }, [setCoupon, getPrice]);

  // Check if form is valid for sticky CTA
  const isFormValid = form.formState.isValid;

  // Track visibility of main submit button to show/hide sticky CTA
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState(true);

  useEffect(() => {
    const checkSubmitButtonVisibility = () => {
      if (submitButtonRef.current) {
        const rect = submitButtonRef.current.getBoundingClientRect();
        // Button is visible if it's within the viewport
        // (top is above viewport bottom AND bottom is below viewport top)
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        setIsSubmitButtonVisible(isInViewport);
      }
    };

    window.addEventListener('scroll', checkSubmitButtonVisibility);
    window.addEventListener('resize', checkSubmitButtonVisibility);
    checkSubmitButtonVisibility();

    return () => {
      window.removeEventListener('scroll', checkSubmitButtonVisibility);
      window.removeEventListener('resize', checkSubmitButtonVisibility);
    };
  }, []);

  // Handle sticky CTA click
  // If form is valid, submit payment; otherwise scroll to payment card
  const handleStickyCTAClick = () => {
    if (isFormValid) {
      handleSubmit();
    } else {
      const paymentCard = document.getElementById('payment-card');
      if (paymentCard) {
        paymentCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <PaymentPageLayout
      header={<Header />}
      hero={<HeroSection showSubhead={false} isMultiDiseaseCohort={isMultiDiseaseCohort} />}
      paymentCard={
        <div className="flex flex-col gap-4">
          {/* Sub-header above the Catch Membership module */}
          {!isTrial && (
            <h2 className="text-xl font-bold text-catch-dark-blue-90">
              A small price to pay for peace of mind
            </h2>
          )}

          <PaymentCard id="payment-card" isSticky={false}>
          {/* Pricing */}
          <PricingHeader
            isTrial={isTrial}
            annualPrice={regularAnnualPrice}
            isLoading={isPriceLoading}
            couponCode={isCouponApplied ? coupon : undefined}
            discountedAnnualPrice={isCouponApplied ? discountedFirstCharge : undefined}
            onRemoveCoupon={handleRemoveCoupon}
          />

          {/* Express Checkout - wallets first as primary option */}
          <ExpressCheckoutSection onConfirm={handleExpressCheckout} />

          {/* Form Fields - manual card entry */}
          <form onSubmit={form.handleSubmit(() => handleSubmit())}>
            <div className="flex flex-col gap-4">
              <EmailField
                registration={form.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={form.formState.errors.email?.message}
                onLeadCapture={handleLeadCapture}
              />

              <NameField
                registration={form.register('name', {
                  required: 'Name is required',
                })}
                error={form.formState.errors.name?.message}
                onNameCapture={handleNameCapture}
              />

              <CardSection
                email={form.watch('email')}
                name={form.watch('name')}
              />

              <ZipCodeField
                registration={form.register('zip', {
                  required: 'Zip code is required',
                  pattern: {
                    value: /^\d{5}$/,
                    message: 'Enter a valid 5-digit zip code',
                  },
                })}
                error={form.formState.errors.zip?.message}
              />

              <CouponInput
                value={coupon}
                onChange={setCoupon}
                onApply={() => getPrice()}
                error={couponValidationError || undefined}
                isExpanded={isCouponExpanded || !!coupon}
                onExpand={() => setIsCouponExpanded(true)}
                isApplied={isCouponApplied}
                isValidating={isPriceLoading}
                couponCode={coupon}
                discountPercent={discountPercent}
                onRemoveCoupon={handleRemoveCoupon}
              />
            </div>

            {/* Submit */}
            <div ref={submitButtonRef} className="mt-6 flex flex-col gap-3">
              <SubmitButton isTrial={isTrial} isSubmitting={isSubmitting} />
              {isTrial && (
                <TrialChargeNotice
                  annualPrice={regularAnnualPrice}
                  discountedFirstCharge={discountedFirstCharge}
                />
              )}
              <LegalDisclaimer />
            </div>
          </form>

          {/* Trust badges (SSL / HIPAA) */}
          <PaymentTrustBadges />
          </PaymentCard>
        </div>
      }
      featuresList={<FeaturesList groups={featureGroups} />}
      testimonials={<TestimonialsSection />}
      privacy={<PrivacySection />}
      faqs={
        <div id="faqs">
          <FAQSection faqSetSlug={isMultiDiseaseCohort ? 'payment-multi-disease' : 'payment-cancer'} />
        </div>
      }
      contactButton={<ContactButton />}
      footer={
        <div className="flex flex-col gap-6">
          <FooterLinks />
          <div className="flex flex-col gap-4">
            <HsaFsaDisclaimer />
            <FooterDisclaimer />
          </div>
        </div>
      }
      stickyMobileCTA={
        <StickyMobileCTA
          isEnabled={isFormValid}
          onClick={handleStickyCTAClick}
          isSubmitting={isSubmitting}
          isVisible={!isSubmitButtonVisible}
        >
          {isTrial ? 'Start my risk assessment' : 'Complete payment'}
        </StickyMobileCTA>
      }
    />
  );
}

/**
 * Wrapper that provides Stripe Elements context
 */
function PaymentPageWithElements({ children }: { children: React.ReactNode }) {
  const { amount } = useStripeAmount();
  const { isTrial } = usePurchaseModel();

  const { stripePromise, stripeOptions } = usePaymentController({
    variant: 'seamless',
    amount: amount ?? 0,
    isTrial,
  });

  const stripeStatus = useStripeLoadStatus(stripePromise);

  if (stripeStatus === 'failed') {
    return <StripeLoadError />;
  }

  return (
    <Elements options={stripeOptions} stripe={stripePromise}>
      {children}
    </Elements>
  );
}

/**
 * Main exported component
 */
export function PaymentPageV2() {
  const setIntercomIsVisible = useSetIntercomIsVisible();

  useOnMount(() => {
    window.scrollTo(0, 0);
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <StripeAmountProvider>
      <PaymentPageWithElements>
        <PaymentForm />
      </PaymentPageWithElements>
    </StripeAmountProvider>
  );
}

export default PaymentPageV2;
